import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

import en from "../i18n/en.json";
import ar from "../i18n/ar.json";
import api from "../api/api";

const LanguageContext = createContext();

// Map of supported languages and their configs
const LANGUAGES = {
  en: { translations: en, dir: "ltr", label: "English", nativeLabel: "English" },
  ar: { translations: ar, dir: "rtl", label: "Arabic", nativeLabel: "العربية" },
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage_] = useState(() => {
    try {
      const saved = localStorage.getItem("language");
      return saved && LANGUAGES[saved] ? saved : "en";
    } catch {
      return "en";
    }
  });

  const setLanguage = useCallback((lang) => {
    if (LANGUAGES[lang]) {
      setLanguage_(lang);
    }
  }, []);

  const config = LANGUAGES[language] || LANGUAGES.en;

  const [dbOverridesByLang, setDbOverridesByLang] = useState({});

  // Apply dir and lang attributes on <html> whenever language changes
  useEffect(() => {
    // Add transition class for smooth visual fade
    document.body.classList.add('lang-switching');
    
    const html = document.documentElement;
    html.setAttribute("dir", config.dir);
    html.setAttribute("lang", language);
    localStorage.setItem("language", language);

    // Remove transition class after the CSS transition duration
    const timer = setTimeout(() => {
      document.body.classList.remove('lang-switching');
    }, 300); // 300ms matches the CSS transition

    return () => clearTimeout(timer);
  }, [language, config.dir]);

  // Optional: merge DB-stored tenant translation overrides into the in-memory JSON map.
  useEffect(() => {
    let cancelled = false;
    async function fetchOverrides() {
      // Avoid refetching the same language during the session.
      if (dbOverridesByLang[language]) return;
      try {
        const res = await api.get(`/common/i18n/translations/?lang=${encodeURIComponent(language)}`);
        const items = res?.data?.items || [];
        const map = {};
        for (const it of items) {
          if (it?.key) map[it.key] = it.value ?? "";
        }
        if (!cancelled) {
          setDbOverridesByLang((prev) => ({ ...prev, [language]: map }));
        }
      } catch (e) {
        // If auth or endpoint fails, keep JSON-only behavior.
      }
    }
    fetchOverrides();
    return () => {
      cancelled = true;
    };
  }, [language, dbOverridesByLang]);

  const mergedTranslations = useMemo(() => {
    const overrides = dbOverridesByLang[language] || {};
    return { ...config.translations, ...overrides };
  }, [config.translations, dbOverridesByLang, language]);

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      translations: mergedTranslations,
      dir: config.dir,
      languages: LANGUAGES,
    }),
    [language, setLanguage, config, mergedTranslations]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  return useContext(LanguageContext);
};

export default LanguageProvider;
